import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Components
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Dashboard from './components/Dashboard/Dashboard';
import ComplianceList from './components/Compliance/ComplianceList';
import ComplianceDetail from './components/Compliance/ComplianceDetail';
import ComplianceForm from './components/Compliance/ComplianceForm';
import UserList from './components/Users/UserList';
import UserForm from './components/Users/UserForm';
import PlantList from './components/Plants/PlantList';
import PlantForm from './components/Plants/PlantForm';
import Profile from './components/Profile/Profile';
import NotFound from './components/Common/NotFound';
import LoadingSpinner from './components/Common/LoadingSpinner';
import Performance from './components/Performance/Performance';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />} />
      <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />
      <Route path="/reset-password" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" replace />} />

      {/* Protected Routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/performance" element={
        <ProtectedRoute>
          <Layout>
            <Performance />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/compliances" element={
        <ProtectedRoute>
          <Layout>
            <ComplianceList />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/compliances/create" element={
        <AdminRoute>
          <Layout>
            <ComplianceForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/compliances/new" element={
        <AdminRoute>
          <Layout>
            <ComplianceForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/compliances/:id" element={
        <ProtectedRoute>
          <Layout>
            <ComplianceDetail />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/compliances/:id/edit" element={
        <AdminRoute>
          <Layout>
            <ComplianceForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/compliances/import" element={
        <AdminRoute>
          <Layout>
            <ComplianceForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin', 'superAdmin']}>
          <Layout>
            <UserList />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/users/create" element={
        <AdminRoute>
          <Layout>
            <UserForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/users/new" element={
        <AdminRoute>
          <Layout>
            <UserForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/users/:id" element={
        <ProtectedRoute allowedRoles={['admin', 'superAdmin']}>
          <Layout>
            <UserForm />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/users/:id/edit" element={
        <AdminRoute>
          <Layout>
            <UserForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/plants" element={
        <ProtectedRoute allowedRoles={['admin', 'superAdmin']}>
          <Layout>
            <PlantList />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/plants/create" element={
        <AdminRoute>
          <Layout>
            <PlantForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/plants/new" element={
        <AdminRoute>
          <Layout>
            <PlantForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/plants/:id" element={
        <ProtectedRoute allowedRoles={['admin', 'superAdmin']}>
          <Layout>
            <PlantForm />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/plants/:id/edit" element={
        <AdminRoute>
          <Layout>
            <PlantForm />
          </Layout>
        </AdminRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />

      {/* 404 Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <div className="App">
        <AppRoutes />
      </div>
    </Router>
  );
}

export default App;
